import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { TrendingDown, TrendingUp, Zap } from 'lucide-react'
import { eventMagnitude, rangeMovers, teamChange } from './data'
import { Avatar, RankDelta, TeamLogo, describeEvent, formatDay, formatSigned } from './ui'

function MoverList({ title, icon: Icon, movers, onSelect, onHover }) {
  return (
    <section className="hv-card hv-movers">
      <header className="hv-card-head">
        <h3><Icon size={16} /> {title}</h3>
      </header>
      <ol>
        {movers.map((m, i) => (
          <motion.li
            key={m.team.id}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04 }}
            onClick={() => onSelect(m.team.id)}
            onMouseEnter={() => onHover(m.team.id)}
            onMouseLeave={() => onHover(null)}
          >
            <TeamLogo team={m.team} size={28} />
            <div className="hv-mover-text">
              <span className="hv-mover-name">{m.team.name}</span>
              <span className="hv-muted hv-small">#{m.from} → #{m.to} · score {formatSigned(m.scoreDelta)}</span>
            </div>
            <RankDelta value={m.delta} size="lg" />
          </motion.li>
        ))}
        {!movers.length && <li className="hv-empty">No movement in this period.</li>}
      </ol>
    </section>
  )
}

// The biggest single changes across the league, newest first.
function LatestMoves({ league, dates, onPick }) {
  const items = useMemo(() => {
    const out = []
    for (const d of [...dates.slice(1)].reverse()) {
      for (const team of league.teams) {
        const change = teamChange(league, team, d)
        if (!change) continue
        for (const e of change.events) out.push({ date: d, team, event: e })
      }
      if (out.length >= 40) break
    }
    // Prefer impactful events, keep chronological order for display.
    return out
      .map((x, i) => ({ ...x, i }))
      .sort((a, b) => eventMagnitude(b.event) - eventMagnitude(a.event) || a.i - b.i)
      .slice(0, 12)
      .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.i - b.i))
  }, [league, dates])

  return (
    <section className="hv-card hv-latest">
      <header className="hv-card-head">
        <div>
          <h3><Zap size={16} /> Latest big moves</h3>
          <p className="hv-muted hv-small">The roster and ranking changes that moved scores the most recently</p>
        </div>
      </header>
      <ul>
        {items.map(({ date, team, event }, i) => {
          const { verb, rest } = describeEvent(event, league)
          return (
            <li key={`${date}-${team.id}-${i}`} onClick={() => onPick({ teamId: team.id, date })}>
              <span className="hv-latest-date">{formatDay(date)}</span>
              <TeamLogo team={team} size={22} />
              <Avatar src={event.image} name={event.name} size={28} />
              <span className="hv-latest-text">
                <b>{team.acronym}</b> {verb.toLowerCase()} {event.kind !== 'note' && event.name} {rest}
              </span>
              {event.impact != null && (
                <span className={`hv-impact-value ${event.impact > 0 ? 'is-up' : event.impact < 0 ? 'is-down' : ''}`}>
                  {formatSigned(event.impact)}
                </span>
              )}
            </li>
          )
        })}
        {!items.length && <li className="hv-empty">No recorded changes in this period.</li>}
      </ul>
    </section>
  )
}

export default function LeagueOverview({ league, dates, onSelect, onHover, onPick }) {
  const movers = useMemo(() => rangeMovers(league, dates), [league, dates])
  const risers = movers.filter(m => m.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 5)
  const fallers = movers.filter(m => m.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 5)
  return (
    <div className="hv-overview">
      <MoverList title="Biggest risers" icon={TrendingUp} movers={risers} onSelect={onSelect} onHover={onHover} />
      <MoverList title="Biggest fallers" icon={TrendingDown} movers={fallers} onSelect={onSelect} onHover={onHover} />
      <LatestMoves league={league} dates={dates} onPick={onPick} />
    </div>
  )
}
